import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { useApp } from '@/contexts/AppContext';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { OnboardingAssistant } from './OnboardingAssistant';

/**
 * OnboardingGate
 *
 * Decides whether to render the onboarding assistant overlay.
 * Renders via ReactDOM.createPortal so it floats above the entire
 * app shell (sidebar, header, content) without z-index conflicts.
 *
 * Trigger logic:
 *   - User must be Forecaster System Administrator (isSystemAdmin)
 *   - User must also have Evo Role = Administrator (systemRole === 'Administrator')
 *   - Must not have completed onboarding (hasCompleted)
 *   - Must not have dismissed in this session (hasDismissed)
 *   - Auto-shows on first load if trigger conditions are met
 */
export const OnboardingGate: React.FC = () => {
  const { isSystemAdmin } = usePermissions();
  const { currentUser } = useApp();
  const {
    isAssistantOpen,
    hasCompleted,
    hasDismissed,
    openAssistant,
  } = useOnboarding();

  // Both conditions must be true: Forecaster role = System Administrator
  // AND Evo (platform) role = Administrator
  const isEligible = isSystemAdmin && currentUser.systemRole === 'Administrator';

  // Auto-trigger: show when eligible and not yet completed/dismissed.
  // hasCompleted is included so the gate re-evaluates after API hydration
  // (the value starts as false, then updates once ['currentUser'] resolves).
  useEffect(() => {
    if (isEligible && !hasCompleted && !hasDismissed) {
      openAssistant();
    }
  }, [isEligible, hasCompleted, hasDismissed, openAssistant]);

  if (!isEligible) return null;
  if (!isAssistantOpen) return null;

  return ReactDOM.createPortal(
    <OnboardingAssistant />,
    document.body,
  );
};
